import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser, fetchUserAttributes, fetchAuthSession } from '../util/amplifyAuth';
import { saveUserProfile, getUserProfile } from '../components/API';
import { setCachedProfile } from '../util/CacheUtil';
import ProfileForm from '../components/ProfileForm/ProfileForm';
import Spinner from '../components/Spinner/Spinner';
import '../styles/Signup.css';
import '../components/SignupFlow/SignupFlow.css';

function CompleteProfile() {
    const [loading, setLoading] = useState(true);
    const [initialData, setInitialData] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [formError, setFormError] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                await getCurrentUser();

                // Doctors skip profile — go straight to doctor view
                const session = await fetchAuthSession();
                const groups = session.tokens?.idToken?.payload['cognito:groups'] || [];
                if (groups.includes('Doctors')) {
                    navigate('/doctor', { replace: true });
                    return;
                }

                // Try to load existing profile data from DynamoDB
                const profile = await getUserProfile().catch(() => null);

                // Fall back to Cognito attributes for clinic-created patients
                let attrs = {};
                try {
                    attrs = await fetchUserAttributes();
                } catch { /* non-critical */ }

                setInitialData({
                    givenName: profile?.givenName || attrs.given_name || '',
                    familyName: profile?.familyName || attrs.family_name || '',
                    birthDate: profile?.birthDate || attrs.birthdate || '',
                    sex: profile?.sex || '',
                    menopausal: profile?.menopausal ?? false,
                    race: profile?.race || '',
                });
            } catch {
                // Not authenticated — send to login
                navigate('/login', { replace: true });
                return;
            } finally {
                setLoading(false);
            }
        })();
    }, [navigate]);

    const handleSave = async (profileData) => {
        setSubmitting(true);
        setFormError('');
        try {
            const result = await saveUserProfile(profileData);
            setCachedProfile(result.profile || profileData);
            navigate('/accept-terms');
        } catch (err) {
            setFormError('Error saving profile: ' + err.message);
            setSubmitting(false);
        }
    };

    if (loading) return null;

    return (
        <div className="signup-page">
            <div className="signup-overlay">
                <div className="signup-modal">
                    <div className="signup-flow-step">
                        <h1>Complete Your Profile</h1>
                        <p className="signup-subtitle">
                            Tell us a little about yourself so we can personalize your results.
                        </p>

                        {formError && (
                            <div className="signup-error">{formError}</div>
                        )}

                        <ProfileForm
                            initialData={initialData}
                            onSave={handleSave}
                            submitting={submitting}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CompleteProfile;
